<script setup lang="ts">
import Button from 'primevue/button';
import Toolbar from 'primevue/toolbar';
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import { ref, type Ref } from 'vue';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import { instance as api, buildUrl } from '@/helpers/api';
import KpSelectField from '@/components/KpSelectField.vue';
import ProduktionAuswertungen, { type ProduktionAuswertungsDaten } from '@/components/ProduktionAuswertungen.vue';
import { errToToast } from '@/helpers/tools';
import { useToast } from 'primevue/usetoast';
import JobList from '@/components/JobList.vue';
import { JOB_TYP, JobCollection } from '@/models/Job';

const loading = ref(false);
const toast = useToast();
const jahr: Ref<number | null> = ref(null);
const kantonId: Ref<number | null> = ref(null);
const typ: Ref<string | null> = ref(null);
const step = ref('1');

const auswertungen = [
    { label: 'Auswertung Stromproduktion', id: 'stromproduktion' },
    { label: 'Auswertung Anzahl Stromproduktionsanlagen', id: 'anzahl_stromproduktionsanlagen' },
    { label: 'Auswertung Anzahl Photovoltaikanlagen', id: 'anzahl_photovoltaikanlagen' },
    { label: 'Auswertung Installierte Leistung Photovoltaikanlagen', id: 'installierte_leistung_photovoltaikanlagen' },
    { label: 'Liste Stromproduktionsanlagen', id: 'liste_stromproduktionsanlagen' },
];

const jobCollection = new JobCollection();

function resetStepper() {
    step.value = '1';
}

async function start(data: ProduktionAuswertungsDaten) {
    loading.value = true;
    step.value = '3';
    try {
        const res = await api().post('/Auswertung/createJob', {
            kanton_id: kantonId.value,
            jahr: jahr.value,
            typ: typ.value,
            aggregation: data.aggregation,
            solarstromanlagen_beruecksichtigen: data.solarstromanlagen_beruecksichtigen
        });

        jahr.value = null;
        kantonId.value = null;
        typ.value = null;
        step.value = '1';
    } catch (e) {
        errToToast(e, toast);
        step.value = '1';
    } finally {
        jobCollection.query();
        loading.value = false;
    }
}

function download() {
    const url = buildUrl('/Auswertung/download', {
        // file: res.data.file,
    });
    window.open(url, '_blank');
}
</script>

<template>
    <Card class="kp-max-content-width">
        <template #header>
            <Toolbar class="toolbar border-none">
                <template #start>
                    <h2><i class="fa-regular fa-table"></i> {{ $t('produktion_auswertungen') }}</h2>
                </template>
            </Toolbar>
        </template>
        <template #content>
            <Stepper v-model:value="step" linear>
                <StepList>
                    <Step value="1">{{ $t('auswertungstyp') }}</Step>
                    <Step value="2">{{ $t('filter') }}</Step>
                    <Step value="3">{{ $t('download') }}</Step>
                </StepList>
                <StepPanels>
                    <StepPanel v-slot="{}" value="1">
                        <div class="formgrid grid align-items-start">
                            <KpSelectField
                                class="col-6"
                                fluid
                                :label="$t('auswertung')"
                                v-model="typ"
                                :options="auswertungen"
                                option-label="label"
                                option-value="id"
                                :disabled="loading"
                            ></KpSelectField>
                            <div class="col-6"></div>
                            <KpMandantKantonSelect
                                class="col-6"
                                v-model="kantonId"
                                fluid
                                :label="$t('kanton')"
                                :disabled="loading"
                            ></KpMandantKantonSelect>
                            <KpJahrSelect
                                class="col-6"
                                v-model="jahr"
                                fluid
                                :startYear="2015"
                                :disabled="loading"
                                :label="$t('jahr')"
                            ></KpJahrSelect>
                        </div>
                        <div class="flex pt-6 justify-content-end">
                            <Button
                                :label="$t('weiter')"
                                :disabled="!typ || !kantonId || !jahr || loading"
                                :loading="loading"
                                icon="fa-solid fa-arrow-right"
                                iconPos="right"
                                @click="() => step = '2'"
                            ></Button>
                        </div>
                    </StepPanel>
                    <StepPanel v-slot="{}" value="2">
                        <ProduktionAuswertungen
                            :typ="typ"
                            :kanton_id="kantonId"
                            :jahr="jahr"
                            @prev="resetStepper"
                            @next="start"
                        ></ProduktionAuswertungen>
                    </StepPanel>
                    <StepPanel value="3">
                        <div class="flex flex-col h-48 align-items-center">
                            <ProgressSpinner
                                v-if="loading"
                                class="ml-0 mr-1rem"
                                style="width: 50px; height: 50px"
                                strokeWidth="4"
                                fill="transparent"
                            />
                            <div>{{ $t('auswertung_wird_erstellt') }}</div>
                        </div>
                        <div class="flex pt-6 justify-content-between">
                            <Button
                                :label="$t('weitere_auswertung')"
                                :disabled="loading"
                                severity="secondary"
                                icon="fa-solid fa-arrow-left"
                                @click="resetStepper()"
                            ></Button>
                        </div>
                    </StepPanel>
                </StepPanels>
            </Stepper>
            <Card>
                <template #header>
                    <h2 class="ml-1rem">{{ $t('laufende_auswertungs_jobs') }}</h2>
                </template>
                <template #content>
                    <JobList :typ="JOB_TYP.AUSWERTUNG_PRODUKTION" :job-collection="jobCollection"></JobList>
                </template>
            </Card>
        </template>
    </Card>
    <ConfirmDialog
        ><template #message="slotProps">
            <div v-html="slotProps.message.message"></div>
        </template>
    </ConfirmDialog>
</template>
